import type { SupplierManagementRow } from '@ps/supplier-kernel';

import type {
  SupplierAllocation,
  SupplierManagedAllocationMember,
  SupplierManagedAllocationRow,
} from '../models';

const FULL_MEMBER_ALLOCATION = 100;

/**
 * Turns the drawer's own working-copy rows into PUT-ready managed rows, carrying
 * their edited quantity/grouping as-is. `completesGoal` lives on the row already;
 * only `bidCpi` still needs joining back from the matching store allocation (a
 * group formed in the drawer has no store match, so its `bidCpi` is null).
 */
export function toManagedRows(
  rows: readonly SupplierManagementRow[],
  storeAllocations: readonly SupplierAllocation[],
): SupplierManagedAllocationRow[] {
  const bidCpiById = new Map(
    storeAllocations.map((allocation) => [
      storeRowId(allocation),
      allocation.bidCpi,
    ]),
  );

  // An inactive supplier is excluded from the PUT — only active suppliers are
  // sent to update.
  return rows
    .filter((row) => row.isSupplierActive)
    .map((row) => ({
      id: row.id,
      name: row.name,
      isGroup: row.isGroup,
      cpi: row.cpi,
      quantity: row.quantity,
      flexibility: row.flexibility,
      bidCpi: bidCpiById.get(row.id) ?? null,
      completesGoal: row.completesGoal,
      members: membersFor(row),
    }));
}

function membersFor(
  row: SupplierManagementRow,
): SupplierManagedAllocationMember[] {
  return row.isGroup
    ? row.members.map((member) => ({
        id: member.id,
        name: member.name,
        allocation: member.allocation,
      }))
    : [
        {
          id: row.id,
          name: row.name,
          allocation: FULL_MEMBER_ALLOCATION,
        },
      ];
}

function storeRowId(allocation: SupplierAllocation): string {
  return allocation.isGroup ? allocation.groupId : String(allocation.id);
}
